import type { ICoord2D } from '@nova/types';

import { normalizeAngleDeg } from '../math-utils';

/**
 * Map angle (degrees) to anchor index 0..n-1.
 * Sectors are centered at i * (360/n) with half-width (360/n)/2; upper bound exclusive with wrap at 0/360.
 */
export function snapAngleToAnchorIndex(angleDeg: number, anchorCount: number): number {
  if (anchorCount <= 1) { return 0; }

  const stepDeg = 360 / anchorCount;
  const halfStep = stepDeg / 2;
  const shifted = normalizeAngleDeg(angleDeg + halfStep);

  return Math.floor(shifted / stepDeg) % anchorCount;
}

/**
 * Unit direction for anchor index i when n anchors evenly divide the circle.
 * Index 0 = east (+x). Uses engine space (x+east, y+south): angle i*(360/n) → (cos, -sin).
 */
export function anchorIndexToDirection(anchorIndex: number, anchorCount: number): ICoord2D {
  if (anchorCount <= 0) { return { x: 1, y: 0 }; }

  const clampedIndex = ((anchorIndex % anchorCount) + anchorCount) % anchorCount;
  const angleDeg = clampedIndex * (360 / anchorCount);
  const angleRad = angleDeg * (Math.PI / 180);

  return { x: Math.cos(angleRad), y: -Math.sin(angleRad) };
}
